import {
  Dispute,
  DisputeReason,
  DisputeStage,
  DisputeStatus,
} from '../types/Dispute'

// Seeded random function for consistent data generation
const seededRandom = (seed: number) => {
  const x = Math.sin(seed) * 10000
  return x - Math.floor(x)
}

// Special dispute configurations - scalable approach for specific test cases
const SPECIAL_DISPUTES: Record<string, Partial<Dispute>> = {
  '1': {
    disputeId: 'dst_hkst9rlmhcmotli5jm',
    transactionId: '8b89fb9a-3fce-4aea-956f-49aa8d033caa',
    amount: 10001,
    status: 'requires_response',
    stage: 'chargeback',
    reason: 'fraudulent',
    reasonDescription: 'Customer claims transaction was fraudulent',
    customerName: 'John Doe',
    customerEmail: 'customer@example.com',
    cardBrand: 'Visa',
    cardLast4: '4242',
    networkReasonCode: '10.4',
  },
  '91': {
    disputeId: 'DP-2024-0091',
    transactionId: 'TXN-2024-9100',
    amount: 153894,
    status: 'requires_response',
    stage: 'chargeback',
    reason: 'duplicate',
    reasonDescription:
      'The customer believes they were charged more than once for the same purchase',
    customerName: 'Sarah Johnson',
    customerEmail: 'sarah.johnson@email.com',
    cardBrand: 'Mastercard',
    cardLast4: '2601',
    networkReasonCode: '4834',
  },
  '92': {
    disputeId: 'DP-2024-0092',
    transactionId: 'TXN-2024-9200',
    amount: 104386,
    status: 'requires_response',
    stage: 'chargeback',
    reason: 'fraudulent',
    reasonDescription: 'Customer claims transaction was fraudulent',
    customerName: 'Michael Chen',
    customerEmail: 'michael.chen@email.com',
    cardBrand: 'Amex',
    cardLast4: '1005',
    networkReasonCode: 'F29',
  },
  '120': {
    reason: 'subscription_canceled',
    reasonDescription: 'Customer claims they canceled their subscription',
    cardBrand: 'Visa',
    networkReasonCode: '13.2',
    stage: 'chargeback',
    status: 'requires_response',
  },
}

export const generateDisputes = (): Dispute[] => {
  const stages: DisputeStage[] = [
    'rfi',
    'pre_chargeback',
    'chargeback',
    'pre_arbitration',
    'arbitration',
  ]
  const statuses: DisputeStatus[] = [
    'requires_response',
    'expired',
    'challenged',
    'accepted',
    'reversed',
    'pending_closure',
    'pending_decision',
    'won',
    'lost',
  ]
  const reasons: DisputeReason[] = [
    'duplicate',
    'fraudulent',
    'subscription_canceled',
    'product_unacceptable',
    'product_not_received',
    'unrecognized',
    'credit_not_processed',
    'incorrect_account_details',
    'insufficient_funds',
    'bank_cannot_process',
    'debit_not_authorized',
    'general',
    'customer_initiated',
    'check_returned',
    'noncompliant',
  ]
  const paymentMethods: ('card' | 'bank_transfer' | 'wallet')[] = [
    'card',
    'bank_transfer',
    'wallet',
  ]
  const cardBrands = ['Visa', 'Mastercard', 'Amex']
  const customerNames = [
    'John Doe',
    'Sarah Johnson',
    'Michael Brown',
    'Emily Davis',
    'David Wilson',
    'Lisa Anderson',
    'Robert Taylor',
    'Jennifer Martinez',
    'William Garcia',
    'Mary Rodriguez',
  ]

  const disputes: Dispute[] = []
  let idCounter = 1

  // Helper function to apply special dispute config if exists
  const applySpecialConfig = (dispute: Dispute): Dispute => {
    const specialConfig = SPECIAL_DISPUTES[dispute.id]
    if (specialConfig) {
      return {
        ...dispute,
        ...specialConfig,
      }
    }
    return dispute
  }

  // First dispute - Special Case ID 1
  const now = new Date()
  const firstDueDate = new Date('2025-12-03T14:12:00Z')
  const firstCreatedAt = new Date('2024-12-01T10:30:00Z')

  const firstDispute: Dispute = {
    id: '1',
    disputeId: 'DP-2024-0001',
    transactionId: 'TXN-2024-0100',
    amount: 10000,
    currency: 'THB',
    status: 'requires_response',
    stage: 'chargeback',
    reason: 'fraudulent',
    reasonDescription: 'Customer claims transaction was fraudulent',
    createdAt: firstCreatedAt.toISOString(),
    dueDate: firstDueDate.toISOString(),
    customerName: 'John Doe',
    customerEmail: 'customer@example.com',
    cardBrand: 'Visa',
    cardLast4: '4242',
    paymentMethod: 'card',
    networkReasonCode: '10.4',
    isChargeback: true,
    lifecycle: [
      {
        id: '1',
        timestamp: firstCreatedAt.toISOString(),
        status: 'requires_response',
        description: 'Dispute created',
      },
      {
        id: '2',
        timestamp: '2024-12-01T10:35:00Z',
        status: 'requires_response',
        description: 'Dispute requires response',
      },
    ],
  }

  disputes.push(applySpecialConfig(firstDispute))
  idCounter++

  // Create disputes due within 24 hours
  const hourlyOffsets = [3, 6, 8, 12, 15, 18, 20, 23]
  hourlyOffsets.forEach((hoursOffset) => {
    const dueDate = new Date(now)
    dueDate.setHours(dueDate.getHours() + hoursOffset)
    const createdAt = new Date(now)
    createdAt.setDate(createdAt.getDate() - 7)

    const paymentMethod = paymentMethods[idCounter % paymentMethods.length]
    let cardBrand = cardBrands[idCounter % cardBrands.length]
    const customerName = customerNames[idCounter % customerNames.length]
    const reason = reasons[idCounter % reasons.length]

    const stageOptions: DisputeStage[] = ['rfi', 'pre_chargeback', 'chargeback']
    let stage = stageOptions[idCounter % stageOptions.length]

    if (stage === 'rfi') cardBrand = 'Amex'
    if (stage === 'pre_chargeback') stage = 'chargeback'

    const amount = Math.floor(
      seededRandom(idCounter * 1000) * (200000 - 1000) + 1000,
    )

    const dispute: Dispute = {
      id: `${idCounter}`,
      disputeId: `DP-2024-${String(idCounter).padStart(4, '0')}`,
      transactionId: `TXN-2024-${String(idCounter * 100).padStart(4, '0')}`,
      amount,
      currency: 'THB',
      status: 'requires_response',
      stage,
      reason,
      reasonDescription: `Customer dispute regarding ${reason}`,
      createdAt: createdAt.toISOString(),
      dueDate: dueDate.toISOString(),
      customerName,
      customerEmail: `${customerName.toLowerCase().replace(' ', '.')}@email.com`,
      cardBrand,
      cardLast4: String(
        Math.floor(seededRandom(idCounter * 100) * 9000) + 1000,
      ),
      paymentMethod,
      networkReasonCode: `${Math.floor(seededRandom(idCounter * 10) * 20)}.${Math.floor(seededRandom(idCounter * 20) * 9)}`,
      isChargeback: stage === 'chargeback',
      lifecycle: [
        {
          id: '1',
          timestamp: createdAt.toISOString(),
          status: 'requires_response',
          description: 'Dispute opened by cardholder',
        },
      ],
    }

    disputes.push(applySpecialConfig(dispute))
    idCounter++
  })

  // Create disputes for various combinations
  stages.forEach((stage) => {
    statuses.forEach((status) => {
      reasons.slice(0, 3).forEach((reason) => {
        const paymentMethod = paymentMethods[idCounter % paymentMethods.length]
        let cardBrand = cardBrands[idCounter % cardBrands.length]
        const customerName = customerNames[idCounter % customerNames.length]

        let dueDate = new Date(now)
        let createdAt = new Date(now)
        let adjustedStage = stage

        if (
          status === 'requires_response' &&
          (stage === 'pre_arbitration' || stage === 'arbitration')
        ) {
          adjustedStage = 'chargeback'
        }
        if (adjustedStage === 'rfi') cardBrand = 'Amex'
        if (
          adjustedStage === 'pre_chargeback' &&
          status === 'requires_response'
        ) {
          adjustedStage = 'chargeback'
        }

        if (status === 'requires_response') {
          const daysOffset = (idCounter % 15) - 5
          dueDate.setDate(dueDate.getDate() + daysOffset)
          createdAt.setDate(createdAt.getDate() - 7)
        } else if (status === 'expired') {
          dueDate.setDate(dueDate.getDate() - ((idCounter % 10) + 1))
          createdAt.setDate(createdAt.getDate() - 20)
        } else {
          dueDate.setDate(dueDate.getDate() + 14)
          createdAt.setDate(createdAt.getDate() - 10)
        }

        const amount = Math.floor(
          seededRandom(idCounter * 1000) * (200000 - 1000) + 1000,
        )

        const dispute: Dispute = {
          id: `${idCounter}`,
          disputeId: `DP-2024-${String(idCounter).padStart(4, '0')}`,
          transactionId: `TXN-2024-${String(idCounter * 100).padStart(4, '0')}`,
          amount,
          currency: 'THB',
          status,
          stage: adjustedStage,
          reason,
          reasonDescription: `Customer dispute regarding ${reason}`,
          createdAt: createdAt.toISOString(),
          dueDate: dueDate.toISOString(),
          customerName,
          customerEmail: `${customerName.toLowerCase().replace(' ', '.')}@email.com`,
          cardBrand,
          cardLast4: String(
            Math.floor(seededRandom(idCounter * 100) * 9000) + 1000,
          ),
          paymentMethod,
          networkReasonCode: `${Math.floor(seededRandom(idCounter * 10) * 20)}.${Math.floor(seededRandom(idCounter * 20) * 9)}`,
          isChargeback:
            adjustedStage === 'chargeback' || adjustedStage === 'arbitration',
          lifecycle: [
            {
              id: '1',
              timestamp: createdAt.toISOString(),
              status: 'requires_response',
              description: 'Dispute opened by cardholder',
            },
          ],
        }

        if (status !== 'requires_response') {
          const updateDate = new Date(createdAt)
          updateDate.setDate(updateDate.getDate() + 2)
          dispute.lifecycle.push({
            id: '2',
            timestamp: updateDate.toISOString(),
            status,
            description: `Status updated to ${status}`,
          })
        }

        disputes.push(applySpecialConfig(dispute))
        idCounter++
      })
    })
  })

  return disputes
}

export const getDisputeById = (id: string): Dispute | undefined => {
  const disputes = generateDisputes()
  return disputes.find((d) => d.id === id)
}
